import React, { useState } from 'react';
import axios from 'axios';
import { User, Phone, CheckCircle2, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

export default function AuthModal({ isOpen, onClose, onLoginSuccess }) {
  const [step, setStep] = useState(1); // 1 = Phone, 2 = OTP
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/login`, { phone });
      toast.success('WhatsApp પર OTP મોકલવામાં આવ્યો છે.');
      setStep(2);
    } catch (err) {
      if (err.response?.status === 429) {
        toast.error('વધુ પડતી વિનંતીઓ. થોડીવાર પછી પ્રયાસ કરો.');
      } else {
         toast.error(err.response?.data?.detail || 'OTP મોકલવામાં નિષ્ફળ.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/verify`, { phone, otp });
      localStorage.setItem('userToken', res.data.token);
      localStorage.setItem('userPhone', res.data.phone);
      toast.success('લૉગિન સફળ રહ્યું.');
      
      // Google Ads Conversion Tracking (Registration/OTP Verify)
      if (window.gtag) {
        window.gtag('event', 'conversion', {'send_to': process.env.REACT_APP_GOOGLE_ADS_SIGNUP_CONVERSION || 'AW-18536313000'});
      }

      if (onLoginSuccess) onLoginSuccess(res.data.phone);
      onClose();
      // Reset state for next open
      setTimeout(() => { setStep(1); setPhone(''); setOtp(''); }, 500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'ખોટો OTP.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
          <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 bg-[#172554]/60 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div 
            initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-white rounded-3xl w-full max-w-sm p-8 relative z-10 shadow-xl overflow-hidden"
          >
            <button onClick={onClose} className="absolute right-6 top-6 text-gray-400 hover:text-black transition-colors">
              <X className="w-5 h-5" />
            </button>
            
            <div className="flex flex-col items-center text-center mb-8">
              <div className="w-16 h-16 bg-[#EBF8FF] rounded-full flex items-center justify-center mb-4 text-[#172554]">
                {step === 1 ? <Phone className="w-8 h-8" /> : <CheckCircle2 className="w-8 h-8 text-green-500" />}
              </div>
              <h2 className="text-2xl font-bold text-[#172554] text-gujarati mb-2">લૉગિન કરો (Login)</h2>
              <p className="text-[#64748B] text-sm text-gujarati">
                {step === 1 ? 'તમારો WhatsApp નંબર દાખલ કરો' : 'WhatsApp પર મળેલો 6-અંકનો OTP દાખલ કરો'}
              </p>
            </div>

            {step === 1 ? (
              <form onSubmit={handleRequestOtp} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#172033] mb-1 text-gujarati">WhatsApp નંબર</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">+91</span>
                    <input 
                      type="tel" required
                      maxLength={10} minLength={10}
                      className="w-full pl-12 pr-4 py-3 border border-[#E2E8F0] rounded-xl focus:ring-2 focus:ring-[#172554] focus:border-transparent outline-none font-medium"
                      value={phone} onChange={e => setPhone(e.target.value.replace(/\D/g, ''))}
                      placeholder="દા.ત. 9876543210"
                    />
                  </div>
                </div>
                <button 
                  type="submit" disabled={loading || phone.length !== 10}
                  className="w-full bg-[#172554] text-white py-3.5 rounded-xl font-bold hover:bg-[#1e3a8a] transition-colors text-gujarati shadow-md flex justify-center items-center gap-2 mt-4 disabled:opacity-50"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'OTP મોકલો (Send OTP)'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerifyOtp} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#172033] mb-1 text-gujarati">OTP</label>
                  <input 
                    type="text" required
                    maxLength={6} minLength={6}
                    className="w-full px-4 py-3 border border-[#E2E8F0] rounded-xl focus:ring-2 focus:ring-[#172554] focus:border-transparent outline-none font-bold text-center tracking-[0.5em] text-lg"
                    value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
                    placeholder="------"
                  />
                </div>
                <button 
                  type="submit" disabled={loading || otp.length !== 6}
                  className="w-full bg-[#F5B800] text-[#172554] py-3.5 rounded-xl font-bold hover:bg-[#FFC928] transition-colors text-gujarati shadow-md flex justify-center items-center gap-2 mt-4 disabled:opacity-50"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'ચકાસો (Verify)'}
                </button>
                <div className="text-center mt-4">
                  <button type="button" onClick={() => setStep(1)} className="text-sm font-bold text-[#172554] hover:underline text-gujarati">
                    નંબર બદલો (Change Number)
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}